import { configureStore } from "@reduxjs/toolkit";
import { Action, combineReducers } from "redux";
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
  persistReducer,
  persistStore,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import { beneficiaryApi } from "./beneficiaryApiSlice";
import beneficiaryReducer from "./beneficiarySlice";
import brandingReducer from "./brandingSlice";
import { employeeContributionApi } from "./employeeApiSlice";
import { legalDocumentsApi } from "./legalDocumentsApiSlice";
import userSlice from "./userSlice";
import { userAccountApi } from "./userAccountApiSlice";
import wizardReducer from "./wizard";

const persistConfig = {
  key: "root",
  storage,
  whitelist: ["user", "wizard"],
  blacklist: [
    beneficiaryApi.reducerPath,
    employeeContributionApi.reducerPath,
    legalDocumentsApi.reducerPath,
    userAccountApi.reducerPath,
  ],
};

const reducers = combineReducers({
  user: userSlice,
  wizard: wizardReducer,
  beneficiary: beneficiaryReducer,
  branding: brandingReducer,
  [beneficiaryApi.reducerPath]: beneficiaryApi.reducer,
  [employeeContributionApi.reducerPath]: employeeContributionApi.reducer,
  [legalDocumentsApi.reducerPath]: legalDocumentsApi.reducer,
  [userAccountApi.reducerPath]: userAccountApi.reducer,
});

// Dispatched on logout to wipe every slice back to its initial state. Passing
// `undefined` as the state makes each child reducer fall back to its default.
export const RESET_STORE = "RESET_STORE";
export const resetStore = () => ({ type: RESET_STORE });

const rootReducer = (
  state: ReturnType<typeof reducers> | undefined,
  action: Action,
): ReturnType<typeof reducers> => {
  if (action.type === RESET_STORE) {
    return reducers(undefined, action);
  }
  return reducers(state, action);
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  devTools: process.env.NODE_ENV !== "production",
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat([
      beneficiaryApi.middleware,
      employeeContributionApi.middleware,
      legalDocumentsApi.middleware,
      userAccountApi.middleware,
    ]),
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof reducers>;

export type AppDispatch = typeof store.dispatch;
